import { useContext, useEffect, useRef, useState } from "react";
import { Footer } from "../components/Footer";
import { Header } from "../components/Header";
import { AppContext } from "../App";
import { CenterMap } from "../components/CenterMap";
import { AvailableAppointments } from "../components/AvailableAppointments";
import {
	orderCentersByDistance,
	getAvailableAppointments,
	sendBooking,
} from "../utils/helpers.js";
import { routes } from "../utils/symbols";
import { t } from "../i18n";
import { Icon } from "../components/Icon";
import { useWizardNavigation } from "../hooks/useWizardNavigation";

const defaultCenter = {
	lat: 40.416775,
	lng: -3.70379,
};

let RALARSA_URL = process.env.REACT_APP_RALARSA_URL;

export const SelectCenter = () => {
	const { viewMap } = useContext(AppContext);
	const { nextPage, previousPage } = useWizardNavigation();

	return (
		<>
			<Header />
			<Content nextPage={nextPage} />
			{viewMap == null && (
				<Footer
					nextPage={nextPage}
					previousPage={previousPage}
					clearOnBack={["centerId", "centerName", "centerCode", "centerAddress"]}
				/>
			)}
		</>
	);
};

const Content = ({ nextPage }) => {
	const {
		data,
		setData,
		centers,
		setCenters,
		setFilteredCenters,
		visibleCenters,
		isTransitioning,
		setIsTransitioning,
		navigate,
		viewMap,
		geolocationFailed,
		bookingSent,
		setBookingSent,
	} = useContext(AppContext);

	const [shouldLoadAppointments, setShouldLoadAppointments] = useState(false);
	const loadingRef = useRef(false);

	// Check conditions whenever relevant state changes
	useEffect(() => {
		if (
			data.journey === "B" &&
			data.locator &&
			visibleCenters.length > 0 &&
			bookingSent
		) {
			setShouldLoadAppointments(true);
		}
	}, [
		data.journey,
		data.locator,
		visibleCenters,
		bookingSent,
		geolocationFailed,
	]);

	// Load appointments when triggered
	useEffect(() => {
		if (!shouldLoadAppointments) return;

		if (loadingRef.current) {
			setShouldLoadAppointments(false);
			return;
		}

		loadingRef.current = true;
		setShouldLoadAppointments(false);

		const centersToLoad = visibleCenters
			.slice(0, 3)
			.filter((center) => !center.temporarilyClosed);

		if (centersToLoad.length === 0) {
			return;
		}

		setLoadingAppointments((prev) => {
			const reset = {};
			centersToLoad.forEach((center) => {
				reset[center.code] = true;
			});
			return { ...prev, ...reset };
		});

		const loadAppointments = () => {
			centersToLoad.forEach((center) => {
				getAvailableAppointments(center.code, data.locator)
					.then((appointments) => {
						setAppointmentsByCenter((prev) => ({
							...prev,
							[center.code]:
								appointments.length > 0 ? appointments : null,
						}));
					})
					.catch((err) => {
						console.error(
							"Error loading appointments for",
							center.code,
							err,
						);
					})
					.finally(() => {
						setLoadingAppointments((prev) => ({
							...prev,
							[center.code]: false,
						}));
					});
			});
		};

		loadAppointments();
	}, [shouldLoadAppointments, data.locator, visibleCenters]);
	const [searchQuery, setSearchQuery] = useState("");
	const [selectedCenter, setSelectedCenter] = useState(null);
	const [mapCenter, setMapCenter] = useState(defaultCenter);
	const [mapIsLoaded, setMapIsLoaded] = useState(false);
	const mapRef = useRef(null);
	const centerRefs = useRef({});
	// Use bookingSent from context instead of local state

	const [viewOption, setViewOption] = useState("list_view");
	const [appointmentsByCenter, setAppointmentsByCenter] = useState({});
	const [loadingAppointments, setLoadingAppointments] = useState({});
	const [searchVersion, setSearchVersion] = useState(0);

	const filterCentersByDistance = (userCoords, maxDistanceKm = 10) => {
		setSearchVersion((prev) => prev + 1);

		const centersWithDistance = orderCentersByDistance(
			centers,
			userCoords,
		).filter((c) => c.distance <= maxDistanceKm);

		setFilteredCenters(centersWithDistance);

		if (centersWithDistance.length > 0) {
			const closest = centersWithDistance[0];
			setSelectedCenter(closest);
			if (!closest.temporarilyClosed) {
				setData((prev) => ({
					...prev,
					centerId: closest.id,
					centerName: closest.name,
					centerCode: closest.code,
				}));
			} else {
				setData((prev) => ({
					...prev,
					centerId: null,
					centerName: null,
					centerCode: null,
				}));
			}

			if (mapRef.current) {
				mapRef.current.panTo({
					lat: closest.latitude,
					lng: closest.longitude,
				});
				mapRef.current.setZoom(12);
			}
		}
	};

	const searchQueryHandler = () => {
		if (!searchQuery.trim()) {
			setFilteredCenters(centers);
			setSearchVersion((prev) => prev + 1);
			return;
		}

		setSearchVersion((prev) => prev + 1);

		const geocoder = new window.google.maps.Geocoder();

		geocoder.geocode(
			{ address: searchQuery + ", Spain" },
			(results, status) => {
				if (status === "OK" && results[0]) {
					const location = results[0].geometry.location;
					const coords = {
						lat: location.lat(),
						lng: location.lng(),
					};
					filterCentersByDistance(coords, 10);
					setMapCenter(coords);
				} else {
					console.warn("Geocoding error:", status);
					setFilteredCenters([]);
				}
			},
		);
	};

	const selectCenter = (center) => async () => {
		setSelectedCenter(center);
		if (!center.temporarilyClosed) {
			setData((prev) => ({
				...prev,
				centerId: center.id,
				centerName: center.name,
				centerCode: center.code,
				centerAddress: center.address,
				reselectingCenter: false,
				centerFromParam: false,
			}));
		} else {
			setData((prev) => ({
				...prev,
				centerId: null,
				centerName: null,
				centerCode: null,
				centerAddress: null,
				reselectingCenter: false,
				centerFromParam: false,
			}));
		}
		setMapCenter({
			lat: center.latitude,
			lng: center.longitude,
		});

		if (!center.temporarilyClosed && data.locator) {
			const sent = await sendBooking(data.locator);
			if (
				sent.remote?.status === 409 ||
				sent.remote?.message?.includes(
					"Hay uno o varios servicios vigentes para la matrícula",
				)
			) {
				setData((prev) => ({ ...prev, bookingExists: true }));
				setIsTransitioning(false);
				navigate(routes.errorPage);
				return;
			}
		}

		navigate(data.reselectingCenter ? routes.selectDateTime : nextPage);
	};

	const highlightCenter = (center) => () => {
		setSelectedCenter(center);
		setData((prev) => ({
			...prev,
			centerId: center.id,
			centerName: center.name,
			centerCode: center.code,
			centerAddress: center.address,
		}));
		setMapCenter({
			lat: center.latitude,
			lng: center.longitude,
		});
	};

	useEffect(() => {
		const query = data.searchWorkcenters ?? "";
		setSearchQuery(query);
	}, [data.searchWorkcenters]);

	useEffect(() => {
		const query = data.searchWorkcenters?.trim();
		if (!query || !mapIsLoaded || centers.length === 0) return;

		const geocoder = new window.google.maps.Geocoder();
		geocoder.geocode(
			{ address: query + ", Spain" },
			(results, status) => {
				if (status === "OK" && results[0]) {
					const location = results[0].geometry.location;
					const coords = { lat: location.lat(), lng: location.lng() };
					filterCentersByDistance(coords, 100);
					setMapCenter(coords);
				}
			},
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data.searchWorkcenters, mapIsLoaded, centers]);

	useEffect(() => {
		if (centers.length === 0) {
			let apiUrl =
				process.env.REACT_APP_API_URL || "http://localhost:3000";

			let controller = new AbortController();
			let options = {
				method: "GET",
				headers: { "Content-Type": "application/json" },
				signal: controller.signal,
			};

			let isMounted = true;

			fetch(`${apiUrl}/query/workcenters`, options)
				.then((res) => res.json())
				.then((newData) => {
					if (isMounted && newData.result === "ok") {
						const workcenters = newData.workcenters.filter(
							(center) => center.active,
						);
						setCenters(workcenters);
						setFilteredCenters(workcenters);
					}
				})
				.catch((err) => {
					if (err.name !== "AbortError") console.error(err);
				});

			return () => {
				isMounted = false;
				controller.abort();
			};
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const sendBookingWithRetry = async (locator, maxRetries = 3) => {
		for (let attempt = 1; attempt <= maxRetries; attempt++) {
			try {
				const sent = await sendBooking(locator);
				if (
					sent.remote?.status === 409 ||
					sent.remote?.message?.includes(
						"Hay uno o varios servicios vigentes para la matrícula",
					)
				) {
					return { success: false, bookingExists: true };
				}
				if (sent.result === "ok") {
					return { success: true, data: sent };
				}
			} catch (err) {
				console.error(`sendBooking error (attempt ${attempt}):`, err);
			}
			if (attempt < maxRetries) {
				await new Promise((resolve) =>
					setTimeout(resolve, 1000 * attempt),
				);
			}
		}
		return { success: false };
	};

	useEffect(() => {
		if (data.journey !== "B" || !data.locator) {
			setBookingSent(false);
			setShouldLoadAppointments(false);
			return;
		}

		let isCancelled = false;
		setBookingSent(false);
		setShouldLoadAppointments(false);

		sendBookingWithRetry(data.locator).then((result) => {
			if (isCancelled) return;

			if (result.bookingExists) {
				setData((prev) => ({ ...prev, bookingExists: true }));
				setIsTransitioning(false);
				navigate(routes.errorPage);
				return;
			}

			if (result.success) {
				setData((prev) => ({
					...prev,
					brand: result.data.remote.marca,
					model: result.data.remote.modelo,
				}));
				setBookingSent(true);
			} else {
				console.error("sendBooking failed after max retries");
				setBookingSent(false);
			}
		});

		return () => {
			isCancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data.locator, data.journey]);

	useEffect(() => {
		if (searchVersion === 0) return;

		setAppointmentsByCenter({});
		setLoadingAppointments({});

		if (
			data.journey === "B" &&
			data.locator &&
			visibleCenters.length > 0 &&
			bookingSent
		) {
			loadingRef.current = false;
			setShouldLoadAppointments(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchVersion]);

	const handleMapChange = () => {
		setSearchVersion((prev) => prev + 1);
	};

	useEffect(() => {
		if (
			viewMap &&
			centers.length > 0 &&
			window.google?.maps &&
			mapIsLoaded
		) {
			const geocoder = new window.google.maps.Geocoder();

			const location = viewMap === "none" ? "Madrid" : viewMap;

			geocoder.geocode(
				{ address: location + ", Spain" },
				(results, status) => {
					if (status === "OK" && results[0]) {
						const location = results[0].geometry.location;
						const coords = {
							lat: location.lat(),
							lng: location.lng(),
						};

						const centersWithDistance = orderCentersByDistance(
							centers,
							coords,
						).filter((c) => c.distance <= 10);

						setFilteredCenters(centersWithDistance);

						if (centersWithDistance.length > 0) {
							const closest = centersWithDistance[0];
							setSelectedCenter(closest);
							if (!closest.temporarilyClosed) {
								setData((prev) => ({
									...prev,
									centerId: closest.id,
									centerName: closest.name,
									centerCode: closest.code,
									centerAddress: closest.address,
								}));
							} else {
								setData((prev) => ({
									...prev,
									centerId: null,
									centerName: null,
									centerCode: null,
									centerAddress: null,
								}));
							}

							if (mapRef.current) {
								mapRef.current.panTo({
									lat: closest.latitude,
									lng: closest.longitude,
								});
								mapRef.current.setZoom(12);
							}
						}

						setMapCenter(coords);
						if (viewMap !== "none") setSearchQuery(viewMap);
					} else {
						console.warn("Geocoding error (viewMap):", status);
						setFilteredCenters([]);
					}
				},
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [viewMap, centers, mapIsLoaded]);

	return (
		<div
			className={
				"content center_selector kensei_03" +
				(isTransitioning ? " fade-in" : "")
			}
		>
			<div className="content_select_center">
				{viewMap == null && (
					<>
						<h2 className="select_center_title">
							<strong>{t("Selecciona un taller")}</strong>
						</h2>
						<div className="center_options">
							<form
								className="search"
								onSubmit={(e) => {
									e.preventDefault();
									searchQueryHandler();
								}}
								role="search"
							>
								<div className="search-icon">
									<Icon
										name="location"
										className="search-icon-img"
									/>
								</div>
								<input
									className="search-input"
									type="text"
									name="searchCenters"
									placeholder={t(
										"Buscar por código postal o provincia",
									)}
									value={searchQuery}
									onChange={(e) => {
										const value = e.target.value;
										const capitalized = value.replace(
											/\b\w/g,
											(char) => char.toUpperCase(),
										);
										setSearchQuery(capitalized);
									}}
								/>
								<button className="search-submit" type="submit">
									{t("Buscar")}
								</button>
							</form>
						</div>
					</>
				)}
				<div className="center_layout">
					<div
						className={
							"center_map" +
							(viewOption === "map_view" ? " isActive" : "")
						}
					>
						<CenterMap
							selectedCenter={selectedCenter}
							setSelectedCenter={setSelectedCenter}
							filterCentersByDistance={filterCentersByDistance}
							selectCenter={selectCenter}
							mapCenter={mapCenter}
							setMapCenter={setMapCenter}
							defaultCenter={defaultCenter}
							mapRef={mapRef}
							setSearchQuery={setSearchQuery}
							setMapIsLoaded={setMapIsLoaded}
							searchQuery={searchQuery}
							onMapChange={handleMapChange}
						/>
					</div>
					<div
						className={
							"center_lists" +
							(viewOption === "list_view" ? " isActive" : "")
						}
					>
						{visibleCenters.length > 0 ? (
							<span>
								{t(
									"Mostrando talleres cercanos a %s",
									searchQuery
										? `"${searchQuery}"`
										: t("tu ubicación"),
								)}
							</span>
						) : (
							<span>{t("No se han encontrado talleres")}</span>
						)}
						<ul className="filter_results">
							{visibleCenters.map((center) => (
								<li
									onClick={
										viewMap === null
											? highlightCenter(center)
											: undefined
									}
									key={center.id}
									ref={(el) =>
										(centerRefs.current[center.id] = el)
									}
									className={
										"filter_results_li kensei_03" +
										(selectedCenter?.id === center.id ||
											data.centerId === center.id
											? " selected"
											: "") +
										(center.temporarilyClosed
											? " closed"
											: "") +
										(viewMap != null ? " isViewMap" : "")
									}
								>
									<h2 className="center_name">
										{center.name}
									</h2>
									<div className="center_address">
										<Icon
											name="location"
											className="address_icon"
										/>
										<p>
											{center.distance != null && (
												<strong>
													{center.distance.toFixed(1)}{" "}
													km
												</strong>
											)}
											{center.address}
										</p>
									</div>
									{center.temporarilyClosed ? (
										<p className="schedules closed">
											{t("Cerrado hasta el")}{" "}
											{new Date(
												center.closureEndDate,
											).toLocaleDateString("es-ES", {
												day: "numeric",
												month: "long",
												year: "numeric",
											})}
										</p>
									) : (
										<p className="schedules">
											{t("Lunes a viernes:")}{" "}
											{center.morningFrom} -{" "}
											{center.morningTo} {t("y")}{" "}
											{center.eveningFrom} -{" "}
											{center.eveningTo}
											{center.saturdayFrom &&
												center.saturdayTo &&
												(center.saturdayFrom !==
													center.saturdayTo ||
													(center.saturdayFrom ===
														"CITA PREVIA" &&
														center.saturdayTo ===
															"CITA PREVIA")) && (
													<>
														<br />
														{t("Sábado:")}{" "}
														{center.saturdayFrom ===
														center.saturdayTo
															? center.saturdayFrom
															: `${center.saturdayFrom} - ${center.saturdayTo}`}
													</>
												)}
										</p>
									)}
									{data.journey === "A" &&
										!center.temporarilyClosed &&
										!viewMap && (
											<div className="center_status">
												{center.availability ===
													"high" && (
													<span className="center_badge highAvailability">
														<Icon name="calendar" />
														Alta disponibilidad
													</span>
												)}
												{/* {center.availability ===
													"medium" && (
														<span className="center_badge lastAppointments">
															<Icon name="appointment" />
															Últimas citas
														</span>
													)} */}
												<button
													className="center_cta"
													onPointerDown={(e) => {
														e.stopPropagation();
														selectCenter(center)();
													}}
												>
													Seleccionar taller
													<Icon name="arrow" />
												</button>
											</div>
										)}
									{data.journey === "B" &&
										!center.temporarilyClosed &&
										!viewMap && (
											<AvailableAppointments
												appointments={
													appointmentsByCenter[
													center.code
													]
												}
												loading={
													loadingAppointments[
													center.code
													] ?? false
												}
												centerCode={center.code}
												selectCenter={selectCenter}
												infoCenter={center}
												viewMap={viewMap}
												onViewMore={(code) => {
												setData((prev) => ({
													...prev,
													centerId: center.id,
													centerName: center.name,
													centerCode: code,
													centerAddress: center.address,
												}));
													navigate(
														routes.selectDateTime,
													);
												}}
												onSelectAppointment={(
													apt,
													code,
												) => {
											const changes = {
												centerId: center.id,
												centerName: center.name,
												centerCode: code,
												centerAddress: center.address,
												date: apt.fullDate,
												startTime: apt.time,
												endTime: apt.time,
												time: apt.time,
											};
												setData((prev) => ({
													...prev,
													...changes,
												}));
												if (data.locator) {
														const apiUrl =
															process.env
																.REACT_APP_API_URL ||
															"http://localhost:3000";
														const token =
															process.env
																.REACT_APP_API_TOKEN;
														fetch(
															`${apiUrl}/bookings/${data.locator}`,
															{
																method: "POST",
																headers: {
																	"Content-Type":
																		"application/json",
																},
																body: JSON.stringify(
																	{
																		...data,
																		...changes,
																		token,
																	},
																),
															},
														).catch((err) =>
															console.error(err),
														);
													}
													navigate(routes.bookingEnd);
												}}
											/>
										)}
									{(data.journey === null || !data.journey) &&
										!center.temporarilyClosed &&
										!viewMap && (
											<button
												className="center_cta"
												onPointerDown={(e) => {
													e.stopPropagation();
													selectCenter(center)();
												}}
											>
												Seleccionar taller
												<Icon name="arrow" />
											</button>
										)}
									{viewMap != null && (
										<div className="center_links">
											<a
												href={
													`${RALARSA_URL}/es/solicita-cita-con-taller-de-lunas?centerCode=` +
													center.code
												}
												title={center.name}
												target="_top"
												rel="noopener noreferrer"
												className="center_booking"
											>
												{t("Cita rápida")}{" "}
											</a>
											<a
												href={
													`${RALARSA_URL}/es/taller-lunas-coche/` +
													center.slug
												}
												title={center.name}
												target="_top"
												rel="noopener noreferrer"
												className="center_btn"
											>
												{t("Ver taller")}{" "}
												<Icon name="arrow" />
											</a>
										</div>
									)}
									{/* 
									<div className="center_status">
										<span className="highAvailability">
											<Icon name="calendar" />
											Alta disponibilidad
										</span>

										<span className="centerOpen">
											<Icon name="clock" />
											Abierto
										</span>
									</div> */}
								</li>
							))}
						</ul>
					</div>
					<div className="view_options">
						<div
							className={
								"view_option" +
								(viewOption === "map_view" ? " isActive" : "")
							}
							onPointerDown={() => setViewOption("list_view")}
						>
							<img
								src="resources/icons/list.svg"
								alt="Lista"
								className="view_option_icon"
							/>
							<span>{t("Lista")}</span>
						</div>
						<div
							className={
								"view_option" +
								(viewOption === "list_view" ? " isActive" : "")
							}
							onPointerDown={() => setViewOption("map_view")}
						>
							<img
								src="resources/icons/map.svg"
								alt="Mapa"
								className="view_option_icon"
							/>
							<span>{t("Mapa")}</span>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};
